// rules.jsx
import React, { useEffect, useMemo, useState } from 'react';
import '../fonts/breuer-headline.css';
import './rules.css';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { initQuestions } from '../../functions/setUserQuestions';
import { selectAdminToken } from '../../admin/sessionSlice';
import { processQuestions } from '../../admin/questionSlice';
import { setBackButtonUrl } from '../uiSlice';
import { getAllStageRulesSections } from '../../functions/stageRules';
import { resolveGameEntryRoute } from '../../functions/stageReportSync';
import {
  validateThemeStageData,
  fireThemeStageDataMissingSwal,
} from '../../functions/validateThemeStageData';
import { fetchThemeData } from '../../admin/themeSlice';

function UserRules() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { status: themeStatus, data: themeData } = useSelector(
    (state) => state.theme
  );
  const { status, user, error } = useSelector((state) => state.auth);
  const adminToken = useSelector(selectAdminToken);
  const [isLoading, setIsLoading] = useState(false);
  const [nextLoading, setNextLoading] = useState(false);

  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );
  
  console.log(themeData);

  useEffect(() => {
    if (!themeData || !user || !adminToken) return;

    // Welcome / full-house themes have no quiz config (no noOfQuestion) — skip setUserQuestions.
    const baseQuestions = parseInt(
      themeData.noOfQuestion ?? themeData.no_of_questions,
      10
    );
    const needsQuizInit = Number.isFinite(baseQuestions) && baseQuestions >= 1;

    setIsLoading(true);

    if (!needsQuizInit) {
      return;
    }

    dispatch(processQuestions())
      .unwrap()
      .then(() => {
        return initQuestions({
          userId: user.userId,
          email: user.email,
          fullName: user.name,
          themeName: themeData.themename,
          token: adminToken,
        });
      })
      .then((data) => {
        console.log('Init response:', data);
      })
      .catch((err) => {
        console.error('Error in process→init chain:', err);
      });
  }, [themeData, user, adminToken, dispatch]);

  useEffect(() => {
    dispatch(setBackButtonUrl("/login?&save=true"));
  }, [status, user, dispatch]);

  const handleNextClick = async (e) => {
    e.preventDefault();
    if (!user || nextLoading) return;

    let storedUser = user;
    try {
      storedUser = JSON.parse(sessionStorage.getItem("userData") || "{}");
    } catch {
      storedUser = user;
    }

    setNextLoading(true);
    try {
      const freshTheme = await dispatch(
        fetchThemeData({ themeId: null })
      ).unwrap();
      const themePayload = freshTheme?.data ?? themeData;
      const stageCheck = validateThemeStageData(themePayload);
      if (!stageCheck.ok) {
        await fireThemeStageDataMissingSwal(stageCheck.missing);
        return;
      }

      const route = await resolveGameEntryRoute({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass: storedUser.sessionId === "demobypass",
      });
      navigate(route, { replace: true });
    } catch (err) {
      console.error("rules handleNextClick:", err);
      navigate("/stage1", { replace: true });
    } finally {
      setNextLoading(false);
    }
  };

  const rulesTextColor = useMemo(() => {
    const raw =
      themeData?.landing_page_title_color ??
      themeData?.landing_page_title_colour;
    if (raw == null || raw === '') return undefined;
    const c = String(raw).trim();
    return c || undefined;
  }, [themeData]);

  const nextButtonTextColor = useMemo(() => {
    const raw = themeData?.button_Textcolor ?? themeData?.button_text_color;
    if (raw == null || raw === '') return undefined;
    const c = String(raw).trim();
    return c || undefined;
  }, [themeData]);

  const nextButtonBackground = useMemo(() => {
    const raw = themeData?.begin_button_color ?? themeData?.button_color;
    if (raw == null || String(raw).trim() === '') return undefined;
    return String(raw).trim();
  }, [themeData]);

  const bonusPointsValue = useMemo(() => {
    const n = parseInt(themeData?.bonus_point, 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [themeData]);

  const stageRuleSections = useMemo(
    () => getAllStageRulesSections(themeData),
    [themeData]
  );

  if (!isLoading) {
  return (
      <div className='quiz-loader-container'>
       <div className="quiz-loader"></div>
      </div>
  );
}

  return (
    <>
      <div className="user-rules-page">
        <div className="user-rules-stack">
          <fieldset
            className="user-rules-fieldset"
            style={rulesTextColor ? { color: rulesTextColor } : undefined}
          >
            <legend className="user-rules-heading">How to Play</legend>
            {stageRuleSections.map((section) => (
              <div key={section.key} className="user-rules-stage-block">
                <h3 className="user-rules-stage-title">{section.label}</h3>
                <ul className="user-rules-list">
                  {section.rules.map((rule, index) => (
                    <li key={`${section.key}-${index}`}>{rule}</li>
                  ))}
                </ul>
              </div>
            ))}
          </fieldset>
          {bonusPointsValue > 0 ? (
            <p
              className="user-rules-bonus"
              style={rulesTextColor ? { color: rulesTextColor } : undefined}
            >
              Bonus points available: {bonusPointsValue}
            </p>
          ) : null}
          <button
            type="button"
            className="user-next-button"
            onClick={handleNextClick}
            style={{
              ...(nextButtonBackground ? { background: nextButtonBackground } : null),
              ...(nextButtonTextColor
                ? { color: nextButtonTextColor, borderColor: nextButtonTextColor }
                : null),
            }}
          >
            {nextLoading ? "Loading…" : "Next"}
          </button>
        </div>
      </div>
    </>
  )
}

export default UserRules;